import { useState, useEffect, useCallback, useRef } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Image as ImageIcon, Loader2, Upload, Trash2, Download, Disc3, MapPin,
  Calendar, Clock, Wine, Plus, X, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

const STYLES = [
  { id: "neon",        label: "Neon Cyber",   accent: "from-fuchsia-500 to-cyan-500" },
  { id: "luxury-gold", label: "Luxury Gold",  accent: "from-amber-400 to-yellow-700" },
  { id: "tropical",    label: "Tropical",     accent: "from-pink-500 to-orange-400" },
  { id: "street",      label: "Street Rave",  accent: "from-slate-700 to-slate-900" },
  { id: "minimal",     label: "Minimal",      accent: "from-slate-200 to-slate-400" },
];
const RATIOS = [
  { id: "9:16", label: "Story · 9:16" },
  { id: "1:1",  label: "Square · 1:1" },
  { id: "4:5",  label: "Portrait · 4:5" },
];

export default function FlyerStudioPage() {
  const { activeBusiness } = useBusiness();
  const [branding, setBranding] = useState(null);
  const [djs, setDjs] = useState([]);
  const [flyers, setFlyers] = useState([]);

  // Form state
  const [title, setTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [address, setAddress] = useState("");
  const [drinks, setDrinks] = useState("");
  const [style, setStyle] = useState("neon");
  const [aspect, setAspect] = useState("9:16");
  const [extraNotes, setExtraNotes] = useState("");
  const [discoLogoId, setDiscoLogoId] = useState(null);
  const [discoLogoPreview, setDiscoLogoPreview] = useState("");
  const [djId, setDjId] = useState(null);
  const [djName, setDjName] = useState("");
  const [djLogoId, setDjLogoId] = useState(null);
  const [djLogoPreview, setDjLogoPreview] = useState("");

  const [generating, setGenerating] = useState(false);
  const [showBrandingDialog, setShowBrandingDialog] = useState(false);
  const [showDJDialog, setShowDJDialog] = useState(false);

  const refreshAll = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      const [b, d, f] = await Promise.all([
        apiAdmin.get(`/branding/${activeBusiness.id}`),
        apiAdmin.get(`/djs?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/flyers?business_id=${activeBusiness.id}`),
      ]);
      setBranding(b.data);
      setDjs(d.data);
      setFlyers(f.data);
      // Pre-fill address from saved branding when not set
      if (b.data?.address && !address) setAddress(b.data.address);
      if (b.data?.logo_media_id && !discoLogoId) {
        setDiscoLogoId(b.data.logo_media_id);
        setDiscoLogoPreview(b.data.logo_url || "");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusiness?.id]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const handleLogoUpload = async (file, target /* 'disco' | 'dj' */) => {
    if (!file || !activeBusiness) return;
    const form = new FormData();
    form.append("file", file);
    form.append("business_id", activeBusiness.id);
    try {
      const { data } = await apiAdmin.post("/media/upload", form);
      if (target === "disco") {
        setDiscoLogoId(data.id);
        setDiscoLogoPreview(data.storage_path);
      } else {
        setDjLogoId(data.id);
        setDjLogoPreview(data.storage_path);
      }
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generate = async () => {
    if (!title.trim()) return toast.error("Event title is required");
    if (!activeBusiness) return;
    setGenerating(true);
    try {
      const { data } = await apiAdmin.post("/flyers/generate", {
        business_id: activeBusiness.id,
        title, event_date: eventDate, event_time: eventTime, address, drink_specials: drinks,
        style, aspect_ratio: aspect, extra_notes: extraNotes,
        disco_logo_media_id: discoLogoId,
        dj_id: djId,
        dj_name: djName,
        dj_logo_media_id: djLogoId,
      });
      toast.success("Flyer generated");
      setFlyers((arr) => [data, ...arr]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (flyerId) => {
    try {
      await apiAdmin.delete(`/flyers/${flyerId}`);
      setFlyers((arr) => arr.filter((f) => f.id !== flyerId));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const aspectClass = aspect === "9:16" ? "aspect-[9/16]" : aspect === "4:5" ? "aspect-[4/5]" : "aspect-square";

  return (
    <div className="space-y-5" data-testid="flyer-studio-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">AI Studio</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Flyer Studio
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Crea flyers ultra-profesionales para tu discoteca y DJs con IA · Logos, dirección y horarios guardados por negocio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowBrandingDialog(true)} data-testid="flyer-edit-branding-btn">
            <Disc3 className="h-4 w-4 mr-1.5" /> Branding del local
          </Button>
          <Button variant="outline" onClick={() => setShowDJDialog(true)} data-testid="flyer-manage-djs-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Gestionar DJs
          </Button>
        </div>
      </div>

      {/* Editor + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT — Editor */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Título del evento</Label>
              <Input
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="REGGAETON NIGHT — Saturday Special"
                className="mt-1.5 text-base font-medium"
                data-testid="flyer-title-input"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Fecha
              </Label>
              <Input value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                placeholder="Sábado · 28 Dic"
                className="mt-1.5" data-testid="flyer-date-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Horarios
              </Label>
              <Input value={eventTime} onChange={(e) => setEventTime(e.target.value)}
                placeholder="10:00 PM · Open Bar hasta 12 AM"
                className="mt-1.5" data-testid="flyer-time-input" />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> Dirección
              </Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="2412 Tremont Ave, The Bronx, NY"
                className="mt-1.5" data-testid="flyer-address-input" />
              <div className="text-[10px] text-slate-400 mt-1">
                Tip: guárdala en "Branding del local" para que se rellene automáticamente.
              </div>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <Wine className="h-3.5 w-3.5" /> Especiales de bebida
              </Label>
              <Textarea
                value={drinks} onChange={(e) => setDrinks(e.target.value)}
                placeholder={"Hennessy $5\nBuchanan's Especial $7\n2x1 en cerveza hasta 11 PM"}
                rows={4} className="mt-1.5 font-mono text-sm" data-testid="flyer-drinks-input"
              />
            </div>

            {/* Logos row */}
            <LogoSlot
              label="Logo de la discoteca"
              preview={discoLogoPreview}
              onChange={(file) => handleLogoUpload(file, "disco")}
              onClear={() => { setDiscoLogoId(null); setDiscoLogoPreview(""); }}
              testid="flyer-disco-logo"
            />
            <LogoSlot
              label="Logo del DJ"
              preview={djLogoPreview}
              onChange={(file) => handleLogoUpload(file, "dj")}
              onClear={() => { setDjLogoId(null); setDjLogoPreview(""); }}
              testid="flyer-dj-logo"
              extra={
                <div className="space-y-2 mt-2">
                  <Input
                    value={djName} onChange={(e) => setDjName(e.target.value)}
                    placeholder="Nombre del DJ (ej: DJ Karol G)"
                    className="text-sm"
                    data-testid="flyer-dj-name-input"
                  />
                  {djs.length > 0 && (
                    <select
                      className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white"
                      value={djId || ""}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        setDjId(id);
                        const dj = djs.find((d) => d.id === id);
                        if (dj) {
                          setDjName(dj.name);
                          if (dj.logo_url) { setDjLogoId(dj.logo_media_id); setDjLogoPreview(dj.logo_url); }
                        }
                      }}
                      data-testid="flyer-dj-saved-select"
                    >
                      <option value="">— DJs guardados —</option>
                      {djs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                </div>
              }
            />
          </div>

          {/* Style + aspect + extra */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Estilo visual</Label>
              <div className="grid grid-cols-5 gap-1.5 mt-1.5">
                {STYLES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStyle(s.id)}
                    className={`rounded-lg p-2 border transition-all flex flex-col items-center gap-1 ${
                      style === s.id ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`flyer-style-${s.id}`}
                  >
                    <div className={`w-full h-6 rounded bg-gradient-to-br ${s.accent}`} />
                    <span className="text-[10px] text-slate-600 truncate">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Proporción</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                {RATIOS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setAspect(r.id)}
                    className={`rounded-lg px-2 py-1.5 border text-xs transition-all ${
                      aspect === r.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`flyer-ratio-${r.id.replace(":","-")}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Notas creativas (opcional)</Label>
            <Textarea
              value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)}
              placeholder="Ej: ambiente reggaeton, mucho neón, modelo bailando, palmeras..."
              rows={2} className="mt-1.5"
              data-testid="flyer-notes-input"
            />
          </div>

          <Button
            onClick={generate}
            disabled={generating}
            className="w-full h-12 text-base bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="flyer-generate-btn"
          >
            {generating ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Generando con IA…</>
            ) : (
              <><Sparkles className="h-5 w-5 mr-2" /> Generar flyer</>
            )}
          </Button>
        </div>

        {/* RIGHT — Latest flyer preview */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Último generado</div>
          {flyers[0] ? (
            <div>
              <div className={`relative ${aspectClass} rounded-xl overflow-hidden bg-slate-900 ring-1 ring-slate-200`}>
                <img src={flyers[0].image_url} alt={flyers[0].title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="mt-3 flex gap-2">
                <Button asChild variant="outline" className="flex-1">
                  <a href={flyers[0].image_url} download={`${flyers[0].title || "flyer"}.png`}>
                    <Download className="h-4 w-4 mr-1.5" /> Descargar
                  </a>
                </Button>
                <Button variant="outline" onClick={() => handleDelete(flyers[0].id)} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-sm text-slate-700 mt-3 font-medium truncate">{flyers[0].title}</div>
              <div className="text-xs text-slate-500 truncate">{flyers[0].event_date} · {flyers[0].event_time}</div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <ImageIcon className="h-10 w-10 text-slate-400 mb-3" strokeWidth={1.25} />
              <div className="text-sm text-slate-500">Genera tu primer flyer →</div>
              <ArrowDown className="h-4 w-4 text-slate-400 mt-2 lg:rotate-[-135deg]" />
            </div>
          )}
        </div>
      </div>

      {/* History gallery */}
      {flyers.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-slate-900">Historial de flyers</div>
            <div className="text-xs text-slate-500">{flyers.length} generados</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {flyers.slice(1).map((f) => (
              <div key={f.id} className="group relative aspect-[9/16] rounded-lg overflow-hidden bg-slate-100 ring-1 ring-slate-200" data-testid={`flyer-history-${f.id}`}>
                <img src={f.image_url} alt={f.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-2 flex flex-col justify-end">
                  <div className="text-[11px] font-medium text-white truncate">{f.title}</div>
                  <div className="text-[10px] text-white/80 truncate">{f.event_date}</div>
                  <div className="flex gap-1 mt-1.5">
                    <a href={f.image_url} download={`${f.title || "flyer"}.png`} className="h-7 flex-1 grid place-items-center rounded bg-white/15 text-white hover:bg-white/25">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button onClick={() => handleDelete(f.id)} className="h-7 w-7 grid place-items-center rounded bg-white/15 text-white hover:bg-rose-500/40">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showBrandingDialog && (
        <BrandingDialog
          business={activeBusiness}
          branding={branding}
          onClose={() => setShowBrandingDialog(false)}
          onSaved={() => { setShowBrandingDialog(false); refreshAll(); }}
        />
      )}
      {showDJDialog && (
        <DJManagerDialog
          business={activeBusiness}
          djs={djs}
          onClose={() => setShowDJDialog(false)}
          onChanged={refreshAll}
        />
      )}
    </div>
  );
}

/* ----- Reusable logo slot ----- */
function LogoSlot({ label, preview, onChange, onClear, testid, extra }) {
  const fileRef = useRef(null);
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="mt-1.5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 grid place-items-center text-slate-500 hover:border-slate-500 hover:text-slate-700 overflow-hidden shrink-0"
          data-testid={`${testid}-pick`}
        >
          {preview ? (
            <img src={preview} alt="logo" className="w-full h-full object-cover" />
          ) : (
            <Upload className="h-5 w-5" strokeWidth={1.5} />
          )}
        </button>
        <div className="text-xs text-slate-500 flex-1">
          {preview ? (
            <button onClick={onClear} className="text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`${testid}-clear`}>
              <X className="h-3 w-3" /> Quitar
            </button>
          ) : (
            <span>PNG / JPG · idealmente cuadrado, fondo transparente.</span>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0])}
        />
      </div>
      {extra}
    </div>
  );
}

/* ----- Branding dialog ----- */
function BrandingDialog({ business, branding, onClose, onSaved }) {
  const [address, setAddress] = useState(branding?.address || "");
  const [instagram, setInstagram] = useState(branding?.instagram || "");
  const [phone, setPhone] = useState(branding?.phone || "");
  const [logoId, setLogoId] = useState(branding?.logo_media_id || null);
  const [logoPreview, setLogoPreview] = useState(branding?.logo_url || "");
  const [saving, setSaving] = useState(false);

  const onLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("business_id", business.id);
    try {
      const { data } = await apiAdmin.post("/media/upload", fd);
      setLogoId(data.id); setLogoPreview(data.storage_path);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiAdmin.patch(`/branding/${business.id}`, {
        address, instagram, phone, logo_media_id: logoId,
      });
      toast.success("Branding guardado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle>Branding del local</DialogTitle>
          <DialogDescription>Datos que se rellenarán automáticamente en cada flyer nuevo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <LogoSlot
            label="Logo del local"
            preview={logoPreview}
            onChange={onLogo}
            onClear={() => { setLogoId(null); setLogoPreview(""); }}
            testid="branding-logo"
          />
          <div>
            <Label className="text-xs text-slate-500">Dirección completa</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5" data-testid="branding-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Instagram</Label>
              <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@royrestaurant" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Teléfono</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 718 555 0000" className="mt-1.5" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="branding-save-btn">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----- DJ manager dialog ----- */
function DJManagerDialog({ business, djs, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [logoId, setLogoId] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [saving, setSaving] = useState(false);

  const onLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("business_id", business.id);
    try {
      const { data } = await apiAdmin.post("/media/upload", fd);
      setLogoId(data.id); setLogoPreview(data.storage_path);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      await apiAdmin.post("/djs", {
        business_id: business.id, name, handle, logo_media_id: logoId,
      });
      toast.success("DJ guardado");
      setName(""); setHandle(""); setLogoId(null); setLogoPreview("");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await apiAdmin.delete(`/djs/${id}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dj-manager-dialog">
        <DialogHeader>
          <DialogTitle>DJs guardados</DialogTitle>
          <DialogDescription>Tu roster reutilizable. Cada flyer puede traer su logo / nombre con un clic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
          {djs.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-4">Aún no hay DJs guardados.</div>
          ) : djs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
              <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                {d.logo_url ? (
                  <img src={d.logo_url} alt={d.name} className="w-full h-full object-cover" />
                ) : (
                  <Disc3 className="h-5 w-5 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                {d.handle && <div className="text-[11px] text-slate-500 truncate">{d.handle}</div>}
              </div>
              <button onClick={() => remove(d.id)} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`dj-${d.id}-delete`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-3 mt-3 space-y-3">
          <div className="text-xs font-medium text-slate-700">Agregar DJ nuevo</div>
          <LogoSlot
            label="Logo / foto"
            preview={logoPreview}
            onChange={onLogo}
            onClear={() => { setLogoId(null); setLogoPreview(""); }}
            testid="new-dj-logo"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Nombre (DJ Karol G)" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-dj-name-input" />
            <Input placeholder="@handle" value={handle} onChange={(e) => setHandle(e.target.value)} />
          </div>
          <Button onClick={add} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-dj-save-btn">
            {saving ? "Guardando…" : "Agregar DJ"}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
